const WORKSHEET_ERROR_CODES = new Set([
  'worksheet_canvas_unavailable',
  'worksheet_render_failed',
  'worksheet_render_empty',
  'worksheet_design_missing',
  'worksheet_pagination_incomplete',
  'worksheet_pdf_failed',
  'worksheet_pdf_empty',
  'worksheet_cloudinary_failed',
  'worksheet_persistence_failed',
]);

// Worksheet errors contain operational metadata only; browser/provider messages
// may include customer data or artwork URLs and must not enter action logs.
export class WorksheetArtifactError extends Error {
  constructor(code, stage, itemIndex = null) {
    const safeCode = WORKSHEET_ERROR_CODES.has(code) ? code : 'worksheet_render_failed';
    super(safeCode);
    this.name = 'WorksheetArtifactError';
    this.code = safeCode;
    this.stage = ['render', 'pagination', 'pdf', 'cloudinary', 'dropbox'].includes(stage) ? stage : 'render';
    this.itemIndex = Number.isInteger(itemIndex) && itemIndex >= 0 ? itemIndex : null;
  }
}

export async function generateRequiredWorksheetPdf({ composePage, toPdf, input, itemIndex, maxPages = 10 }) {
  const item = input.item;
  const totalColors = Object.keys(item.sizeMatrices || {}).length;
  const totalAreas = (item.selectedPrintAreas || []).filter(Boolean).length;
  const pagePngs = [];
  let startColorIndex = 0;
  let startAreaIndex = 0;
  let complete = false;

  for (let pageIndex = 0; pageIndex < maxPages; pageIndex += 1) {
    let result;
    try {
      result = await composePage({ ...input, returnMeta: true, startColorIndex, startAreaIndex });
    } catch (error) {
      throw new WorksheetArtifactError(
        error instanceof WorksheetArtifactError ? error.code : 'worksheet_render_failed',
        'render',
        itemIndex,
      );
    }
    if (typeof result?.dataUrl !== 'string' || !/^data:image\/png;base64,[A-Za-z0-9+/=]+$/.test(result.dataUrl)) {
      throw new WorksheetArtifactError('worksheet_render_empty', 'render', itemIndex);
    }
    const colors = result.consumed?.colors;
    const areas = result.consumed?.areas;
    if (!Number.isInteger(colors) || colors < 0 || !Number.isInteger(areas) || areas < 0
      || startColorIndex + colors > totalColors || startAreaIndex + areas > totalAreas) {
      throw new WorksheetArtifactError('worksheet_pagination_incomplete', 'pagination', itemIndex);
    }
    pagePngs.push(result.dataUrl);
    startColorIndex += colors;
    startAreaIndex += areas;
    if (startColorIndex === totalColors && startAreaIndex === totalAreas) {
      complete = true;
      break;
    }
    if (colors === 0 && areas === 0) break;
  }
  if (!complete) {
    throw new WorksheetArtifactError('worksheet_pagination_incomplete', 'pagination', itemIndex);
  }

  let pdfDataUrl;
  try {
    pdfDataUrl = await toPdf(pagePngs, { widthPx: 1240, heightPx: 1754 });
  } catch (_) {
    throw new WorksheetArtifactError('worksheet_pdf_failed', 'pdf', itemIndex);
  }
  if (typeof pdfDataUrl !== 'string' || !/^data:application\/pdf(?:;[^,]*)?;base64,[A-Za-z0-9+/=]+$/.test(pdfDataUrl)) {
    throw new WorksheetArtifactError('worksheet_pdf_empty', 'pdf', itemIndex);
  }
  return pdfDataUrl;
}

export function assertWorksheetCloudinaryUploads(worksheetUploads, cloudinaryResult) {
  for (const worksheet of worksheetUploads) {
    const uploaded = cloudinaryResult?.files?.find((file) => file?.method === 'worksheet'
      && file.itemIndex === worksheet.itemIndex
      && file.productSku === worksheet.product);
    if (cloudinaryResult?.ok !== true || !uploaded?.secure_url || uploaded.error) {
      throw new WorksheetArtifactError('worksheet_cloudinary_failed', 'cloudinary', worksheet.itemIndex);
    }
  }
}

export function assertWorksheetPersistence(worksheetUploads, result) {
  for (const worksheet of worksheetUploads) {
    const key = `${worksheet.product}--item-${worksheet.itemIndex + 1}`;
    if (result?.ok !== true || !result?.dropbox?.worksheet_links?.[key]) {
      throw new WorksheetArtifactError('worksheet_persistence_failed', 'dropbox', worksheet.itemIndex);
    }
  }
}
